import { css } from "styled-system/css";

interface GaugeMeterProps {
  value: number; // 0-100
  // Zone boundaries (upper bounds). Defaults match momentum bands.
  zones?: { low: number; mid: number };
}

// Fuel-gauge meter: a flat horizontal track with three zones, tick marks,
// and a needle. E (empty) to F (full). Industrial, no arcs, no gradients —
// stays inside the design system's chart rules.
export function GaugeMeter({ value, zones = { low: 45, mid: 65 } }: GaugeMeterProps) {
  const clamped = Math.min(100, Math.max(0, value));
  const ticks = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

  return (
    <div className={styles.wrap}>
      <div className={styles.track}>
        <div
          className={styles.zoneLow}
          style={{ width: `${zones.low}%` }}
          aria-hidden="true"
        />
        <div
          className={styles.zoneMid}
          style={{ left: `${zones.low}%`, width: `${zones.mid - zones.low}%` }}
          aria-hidden="true"
        />
        <div
          className={styles.zoneHigh}
          style={{ left: `${zones.mid}%`, width: `${100 - zones.mid}%` }}
          aria-hidden="true"
        />
        {ticks.map((t) => (
          <div
            key={t}
            className={styles.tick}
            data-major={t % 50 === 0}
            style={{ left: `${t}%` }}
            aria-hidden="true"
          />
        ))}
        <div
          className={styles.needle}
          style={{ left: `${clamped}%` }}
          role="meter"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={clamped}
        />
      </div>
      <div className={styles.labels} aria-hidden="true">
        <span>E</span>
        <span className={styles.midLabel}>level with last period</span>
        <span>F</span>
      </div>
    </div>
  );
}

const styles = {
  wrap: css({ width: "100%" }),
  track: css({
    position: "relative",
    height: "28px",
    borderRadius: "sm",
    overflow: "visible",
    border: "1px solid",
    borderColor: "brand.guardrail",
    bg: "bg.muted",
  }),
  zoneLow: css({
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 0,
    bg: "#ffe9ea", // red-50 — cautionary zone
  }),
  zoneMid: css({
    position: "absolute",
    top: 0,
    bottom: 0,
    bg: "#fff6e6", // yellow-50
  }),
  zoneHigh: css({
    position: "absolute",
    top: 0,
    bottom: 0,
    bg: "#e3faec", // green-50
  }),
  tick: css({
    position: "absolute",
    bottom: 0,
    width: "1px",
    height: "8px",
    bg: "brand.guardrail",
    transform: "translateX(-0.5px)",
    "&[data-major='true']": {
      height: "14px",
      bg: "#9d9d97",
    },
  }),
  needle: css({
    position: "absolute",
    top: "-5px",
    bottom: "-5px",
    width: "4px",
    bg: "brand.tarmac",
    borderRadius: "1px",
    transform: "translateX(-2px)",
    transition: "left 300ms cubic-bezier(0.2, 0, 0, 1)",
    _after: {
      content: '""',
      position: "absolute",
      top: "-6px",
      left: "50%",
      transform: "translateX(-50%)",
      borderLeft: "5px solid transparent",
      borderRight: "5px solid transparent",
      borderTop: "6px solid #1e1c18",
    },
  }),
  labels: css({
    display: "flex",
    justifyContent: "space-between",
    mt: 2,
    fontSize: "xs",
    fontWeight: "bold",
    color: "fg.muted",
  }),
  midLabel: css({
    fontWeight: "normal",
    color: "fg.subtle",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    fontSize: "2xs",
    alignSelf: "center",
  }),
};
